import React from "react";
import { Modal, Row, Col, Typography, Progress, Card, Divider, Space, Badge } from "antd";
import {
  UserOutlined,
  MailOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseOutlined
} from "@ant-design/icons";
import { motion, AnimatePresence } from "framer-motion";

const { Title, Text } = Typography;

interface SuiviModalProps {
  visible: boolean;
  onClose: () => void;
  user: any;
}

const SuiviModal: React.FC<SuiviModalProps> = ({ visible, onClose, user }) => {
  if (!user) return null;

  const dailyUsed = user.emails_sent_today || 0;
  const monthlyUsed = user.emails_sent_this_month || 0;
  const totalUsed = user.emails_sent_total || 0;
  const dailyQuota = user.daily_email_quota || 0;
  const monthlyQuota = user.monthly_email_quota || 0;

  const dailyPercent = dailyQuota ? Math.min((dailyUsed / dailyQuota) * 100, 100) : 0;
  const monthlyPercent = monthlyQuota ? Math.min((monthlyUsed / monthlyQuota) * 100, 100) : 0;
  const dailyRemaining = Math.max(dailyQuota - dailyUsed, 0);
  const monthlyRemaining = Math.max(monthlyQuota - monthlyUsed, 0);

  const getQuotaStatus = (percent: number) => {
    if (percent >= 90) return { color: "#ef4444", status: "critical" };
    if (percent >= 70) return { color: "#f97316", status: "warning" };
    return { color: "#4bb0c0", status: "good" };
  };

  const dailyStatus = getQuotaStatus(dailyPercent);
  const monthlyStatus = getQuotaStatus(monthlyPercent);

  return (
    <AnimatePresence>
      {visible && (
        <Modal
          open={visible}
          onCancel={onClose}
          footer={null}
          width={750}
          bodyStyle={{ padding: 0 }}
          closable={false}
          centered
          className="quota-modal"
        >
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              background: "linear-gradient(135deg, #4bb0c0 0%, #2A919D 100%)",
              padding: "24px 32px",
              position: "relative"
            }}
          >
            <button
              onClick={onClose}
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                background: "rgba(255,255,255,0.2)",
                border: "none",
                borderRadius: "50%",
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "white",
                transition: "all 0.3s"
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.3)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.2)")}
            >
              <CloseOutlined />
            </button>

            <Row align="middle" gutter={[16, 16]}>
              <Col flex="none">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  style={{
                    width: 64,
                    height: 64,
                    background: "rgba(255,255,255,0.2)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <UserOutlined style={{ fontSize: 32, color: "white" }} />
                </motion.div>
              </Col>

              <Col flex="auto">
                <Space direction="vertical" size={4}>
                  <Title level={3} style={{ margin: 0, color: "white" }}>
                    {user.name} {user.firstname}
                  </Title>
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                    <Badge
                      status="success"
                      text={
                        <Text style={{ color: "rgba(255,255,255,0.9)" }}>
                          {user.user_type === "personne" ? "Particulier" : "Entreprise"}
                        </Text>
                      }
                    />
                    <Divider type="vertical" style={{ background: "rgba(255,255,255,0.3)", height: "auto" }} />
                    <Text style={{ color: "rgba(255,255,255,0.9)" }}>
                      <MailOutlined style={{ marginRight: 4 }} />
                      Suivi des envois
                    </Text>
                  </div>
                </Space>
              </Col>
            </Row>
          </motion.div>

          {/* Contenu principal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{ padding: "32px" }}
            className="modal-content"
          >
            {/* Section Progression */}
            <div style={{ marginBottom: 32 }}>
              <Title level={4} style={{ marginBottom: 20, color: "#374151" }}>
                Progression des quotas
              </Title>

              <Row gutter={[24, 24]}>
                {[
                  {
                    title: "Quotidien",
                    used: dailyUsed,
                    quota: dailyQuota,
                    percent: dailyPercent,
                    status: dailyStatus,
                    icon: <ClockCircleOutlined />
                  },
                  {
                    title: "Mensuel",
                    used: monthlyUsed,
                    quota: monthlyQuota,
                    percent: monthlyPercent,
                    status: monthlyStatus,
                    icon: <CalendarOutlined />
                  }
                ].map((item, idx) => (
                  <Col xs={24} sm={12} md={12} lg={12} key={idx}>
                    <motion.div
                      whileHover={{ y: -4, boxShadow: "0 12px 20px rgba(0,0,0,0.1)" }}
                      initial={{ opacity: 0, x: idx === 0 ? -20 : 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                    >
                      <Card
                        bordered={false}
                        style={{
                          borderRadius: 16,
                          background: "white",
                          border: `1px solid ${item.status.color}20`
                        }}
                      >
                        <Space direction="vertical" size={16} style={{ width: "100%" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Space>
                              <div
                                style={{
                                  background: `${item.status.color}15`,
                                  padding: 8,
                                  borderRadius: 8
                                }}
                              >
                                {React.cloneElement(item.icon, {
                                  style: { color: item.status.color, fontSize: 20 }
                                })}
                              </div>
                              <Text strong style={{ fontSize: 16 }}>
                                {item.title}
                              </Text>
                            </Space>
                            {item.status.status === "critical" ? (
                              <ExclamationCircleOutlined style={{ color: item.status.color }} />
                            ) : (
                              <CheckCircleOutlined style={{ color: item.status.color }} />
                            )}
                          </div>

                          <div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginBottom: 8
                              }}
                            >
                              <Text type="secondary">Utilisé</Text>
                              <Text strong>
                                {item.used} / {item.quota}
                              </Text>
                            </div>
                            <Progress
                              percent={item.percent}
                              strokeColor={item.status.color}
                              trailColor="#f3f4f6"
                              size={["100%", 10]}
                              showInfo={false}
                              style={{ marginBottom: 4 }}
                            />
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: 12,
                                color: "#6b7280"
                              }}
                            >
                              <Text>0%</Text>
                              <Text>100%</Text>
                            </div>
                          </div>

                          <Divider style={{ margin: "8px 0" }} />

                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Text type="secondary">Restant</Text>
                            <Text strong style={{ fontSize: 18, color: item.status.color }}>
                              {item.quota - item.used}
                            </Text>
                          </div>
                        </Space>
                      </Card>
                    </motion.div>
                  </Col>
                ))}
              </Row>
            </div>

            {/* Section Statistiques */}
            <div>
              <Title level={4} style={{ marginBottom: 20, color: "#374151" }}>
                Statistiques globales
              </Title>

              <Row gutter={[16, 16]}>
                {[
                  {
                    title: "Total envoyés",
                    value: totalUsed,
                    icon: <MailOutlined />,
                    color: "#4bb0c0",
                    description: "Depuis le début"
                  },
                  {
                    title: "Disponible aujourd'hui",
                    value: dailyRemaining,
                    icon: <ClockCircleOutlined />,
                    color: dailyRemaining > 0 ? "#4bb0c0" : "#ef4444",
                    description: "Emails restants"
                  },
                  {
                    title: "Disponible ce mois",
                    value: monthlyRemaining,
                    icon: <CalendarOutlined />,
                    color: monthlyRemaining > 0 ? "#4bb0c0" : "#ef4444",
                    description: "Emails restants"
                  }
                ].map((item, idx) => (
                  <Col xs={24} sm={12} md={8} key={idx}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      whileHover={{ scale: 1.05 }}
                      transition={{ delay: 0.2 + idx * 0.05 }}
                    >
                      <Card
                        bordered={false}
                        hoverable
                        style={{
                          borderRadius: 12,
                          textAlign: "center",
                          background: "white",
                          border: `1px solid ${item.color}20`,
                          height: "100%"
                        }}
                        bodyStyle={{ padding: 24 }}
                      >
                        <Space direction="vertical" size={12} style={{ width: "100%" }}>
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              background: `${item.color}15`,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              margin: "0 auto"
                            }}
                          >
                            {React.cloneElement(item.icon, { style: { fontSize: 24, color: item.color } })}
                          </div>

                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {item.title}
                            </Text>
                            <Title level={2} style={{ margin: "8px 0", color: item.color }}>
                              {item.value.toLocaleString()}
                            </Title>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {item.description}
                            </Text>
                          </div>
                        </Space>
                      </Card>
                    </motion.div>
                  </Col>
                ))}
              </Row>
            </div>

            {/* Footer */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              style={{
                marginTop: 32,
                padding: 16,
                background: "#f8fafc",
                borderRadius: 12,
                borderLeft: "4px solid #4bb0c0"
              }}
            >
              <Space>
                <ExclamationCircleOutlined style={{ color: "#4bb0c0" }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Les quotas sont réinitialisés quotidiennement à minuit et mensuellement le premier jour du mois.
                </Text>
              </Space>
            </motion.div>
          </motion.div>
        </Modal>
      )}
    </AnimatePresence>
  );
};

export default SuiviModal;
